import {
	ReactElement,
	RefObject,
	useEffect,
	useRef,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useResizeObserver
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { BusyIndicator } from "client/Components";
import classNames from "classnames";
import styles from "./HubTemplatePreview.module.less";
import { HubMediaSelectLayout, ImageMediaContentOriginalImage } from "../HubMediaSelect/HubMediaSelectLayout";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { ContentViewer } from "../Builder/Viewer/ContentViewer";
import { HubItemMediaKind } from "client/Api/Hub/common";
import { never } from "client_legacy/ndaw/errors";
import { ScaleToFitResult, useScaleToFit } from "client/Hooks/useScaleToFit";
import { HubTemplateDetailsViewModel, preview } from "client/Api/Hub/Templates/hubTemplates";
import { HubItemLayoutDetailsViewModel, HubItemMediaDetailsViewModel } from "client/Api/Hub/common.details";
import { HubImageMediaKind } from "client/Api/Hub/common.composer";
import { setTrackingId } from "client/Misc/utils";

export type HubTemplatePreviewProps = {
	kind: HubItemKind;
	templateId: number | nil;
	trackingId: string;
};

export const HubTemplatePreview = (props: HubTemplatePreviewProps): ReactElement => {
	const {
		kind,
		templateId,
		trackingId
	} = props;

	const [state, setState] = useState<State>({
		loadingStatus: "not-loaded"
	});

	const alertManager: AlertManager = useAlerts();
	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const {
		width: rootWidth
	} = useResizeObserver(rootRef);

	useEffect(
		(): EffectWithDestructorResult => {
			if (templateId === undefined) {
				return;
			}

			if (templateId === null) {
				setState({
					loadingStatus: "ready",
					title: null,
					layout: null,
					media: {
						mediaKind: HubItemMediaKind.None
					}
				});

				return;
			}

			async function fetchTemplate(templateId: number, signal: AbortSignal): Promise<void> {
				try {
					setState({
						loadingStatus: "loading"
					});

					const viewModel: HubTemplateDetailsViewModel = await preview(
						templateId,
						signal
					);

					const {
						title,
						media,
						layout
					} = viewModel;

					setState({
						loadingStatus: "ready",
						title,
						media,
						layout
					});
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setState({
						loadingStatus: "not-loaded"
					});

					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();

			fetchTemplate(templateId, cancellation.signal);

			return (): void => cancellation.abort();
		},
		[
			alertManager,
			templateId
		]
	);

	const renderContent = (): ReactElement | null => {
		if (state.loadingStatus !== "ready") {
			return null;
		}

		const {
			layout,
			media,
			title
		} = state;

		return (
			<Content
				kind={kind}
				title={title}
				layout={layout}
				media={media}
				rootWidth={rootWidth}
			/>
		);
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
			ref={rootRef}
		>
			{state.loadingStatus === "loading"
				? <BusyIndicator />
				: renderContent()
			}
		</div>
	);
};
HubTemplatePreview.displayName = "HubTemplatePreview";

type State = {
	loadingStatus: "ready";
	title: string | null;
	media: HubItemMediaDetailsViewModel;
	layout: HubItemLayoutDetailsViewModel | null;
} | {
	loadingStatus: "loading";
} | {
	loadingStatus: "not-loaded";
};

type ContentProps = {
	kind: HubItemKind;
	title: string | null;
	media: HubItemMediaDetailsViewModel;
	layout: HubItemLayoutDetailsViewModel | null;
	rootWidth: number;
};

const Content = (props: ContentProps): ReactElement => {
	const {
		kind,
		media,
		layout,
		title,
		rootWidth
	} = props;

	const contentRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const scaleToFitResult: ScaleToFitResult | null = useScaleToFit(
		contentRef,
		rootWidth
	);

	return (
		<>
			<div
				style={scaleToFitResult
					? {
						height: scaleToFitResult.scaledDimensions
							.height
					}
					: undefined
				}
			/>
			<div
				style={scaleToFitResult
					? {
						transform: `scale(${scaleToFitResult.scale})`
					} : undefined
				}
				className={styles.content}
				ref={contentRef}
			>
				<h2
					className={classNames(
						styles.title,
						{ [styles.title_placeholder]: !title }
					)}
				>
					{title
						? title
						: localize("_EnterTitle")}
				</h2>
				<div className={styles.media}>
					<Media
						kind={kind}
						media={media}
					/>
				</div>
				{layout && (
					<ContentViewer
						hubItemKind={kind}
						layout={layout}
					/>
				)}
			</div>
		</>
	);
};

Content.displayName = "Content";

type MediaProps = {
	kind: HubItemKind;
	media: HubItemMediaDetailsViewModel;
};

const Media = (props: MediaProps): ReactElement => {
	const {
		kind,
		media
	} = props;

	const renderMedia = (): ReactElement => {
		switch(media.mediaKind) {
			case HubItemMediaKind.None:
				return (
					<HubMediaSelectLayout.NoneMediaContent />
				);
			case HubItemMediaKind.Image:
				const originalImage: ImageMediaContentOriginalImage = {
					mediaKind: media.mediaKind,
					imageKind: HubImageMediaKind.BlobId,
					imageId: media.imageId,
					imageName: media.imageName,
					attribution: media.attribution
				};

				return (
					<HubMediaSelectLayout.ImageMediaContent
						hubItemKind={kind}
						image={{ isSet: false }}
						originalImageMedia={originalImage}
					/>
				);
			case HubItemMediaKind.Video:
				return (
					<HubMediaSelectLayout.VideoMediaContent
						videoUrl={media.videoUrl}
					/>
				);
			case HubItemMediaKind.Embed:
				return (
					<HubMediaSelectLayout.EmbedMediaContent
						embedCode={media.embedCode}
					/>
				);
			default:
				never(media);
		}
	};

	return (
		<HubMediaSelectLayout
			trackingId="template-preview-media"
			isReadonly
		>
			{renderMedia()}
		</HubMediaSelectLayout>
	);
};
Media.displayName = "Media";
